import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, Calendar, FileText, TrendingUp, LogOut, Shield, Plus, Trash2, Edit3, Loader2, ArrowLeft, KeyRound } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AdminEbookManager } from "@/components/AdminEbookManager";
import { AdminPayments } from "@/components/admin/AdminPayments";
import { AdminCourses } from "@/components/admin/AdminCourses";
import { AdminReviews } from "@/components/admin/AdminReviews";
import { AdminImmigration } from "@/components/admin/AdminImmigration";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { toast } from "sonner";

const ROLE_LABEL = { user: "User", admin: "Admin", sub_admin: "LNI Admin", super_admin: "Super Admin" };
const ROLE_BADGE = {
  super_admin: "bg-brand-red hover:bg-brand-red",
  sub_admin: "bg-brand-gold text-brand-navy hover:bg-brand-gold",
  admin: "bg-brand-red hover:bg-brand-red",
  user: "bg-brand-navy hover:bg-brand-navy",
};

const StatCard = ({ Icon, label, value, color = "#D90429" }) => (
  <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="rounded-3xl bg-white border border-brand-navy/10 p-6 card-hover">
    <div className="w-12 h-12 rounded-2xl grid place-items-center mb-4" style={{ background: color + "18", color }}>
      <Icon className="w-6 h-6" />
    </div>
    <div className="font-heading font-black text-3xl text-brand-navy">{value}</div>
    <div className="text-xs uppercase tracking-widest text-brand-navy/50 mt-1 font-semibold">{label}</div>
  </motion.div>
);

const emptyPost = { slug: "", title: "", excerpt: "", body: "", hero_image: "", tags: "", published: true };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [postForm, setPostForm] = useState(emptyPost);
  const [savingPost, setSavingPost] = useState(false);

  const isSuper = user?.role === "super_admin";

  const load = async () => {
    setLoading(true);
    try {
      const [s, b, u, p] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/users"),
        api.get("/admin/blog"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setUsers(u.data);
      setPosts(p.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const changeRole = async (uid, role) => {
    try {
      await api.patch(`/admin/users/${uid}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.user_id === uid ? { ...x, role } : x)));
      toast.success(`Role updated → ${ROLE_LABEL[role]}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Remove this user permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/users/${uid}`);
      setUsers((prev) => prev.filter((x) => x.user_id !== uid));
      toast.success("User removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openNewPost = () => { setEditing({ mode: "new" }); setPostForm(emptyPost); };
  const openEditPost = (p) => {
    setEditing({ mode: "edit", slug: p.slug });
    setPostForm({ ...p, tags: (p.tags || []).join(", ") });
  };

  const savePost = async () => {
    setSavingPost(true);
    try {
      const body = {
        slug: postForm.slug.trim(), title: postForm.title.trim(),
        excerpt: postForm.excerpt.trim(), body: postForm.body,
        hero_image: postForm.hero_image || null,
        tags: postForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        published: !!postForm.published,
      };
      if (editing.mode === "new") await api.post("/admin/blog", body);
      else await api.put(`/admin/blog/${editing.slug}`, body);
      toast.success("Post saved");
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingPost(false); }
  };

  const deletePost = async (slug) => {
    if (!window.confirm(`Delete "${slug}"?`)) return;
    try { await api.delete(`/admin/blog/${slug}`); await load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading) {
    return <div className="min-h-screen grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-brand-red" /></div>;
  }

  const tabTriggerClass = "rounded-full data-[state=active]:bg-brand-red data-[state=active]:text-white px-5 font-semibold";

  return (
    <div className="min-h-screen bg-brand-muted-bg">
      <header className="bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="admin-back-home" className="text-white/60 hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="w-10 h-10 rounded-xl bg-brand-red grid place-items-center font-black">L</div>
            <div>
              <div className="font-heading font-bold text-lg">Admin Console</div>
              <div className="text-xs text-brand-gold flex items-center gap-1" data-testid="admin-role-label">
                <Shield className="w-3 h-3" /> {ROLE_LABEL[user?.role] || "Admin"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-sm text-white/70">{user.email}</div>
            <ChangePasswordDialog
              mode="self"
              trigger={
                <Button data-testid="admin-change-password-btn" variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white hover:text-brand-navy">
                  <KeyRound className="w-4 h-4 mr-1" /> Change password
                </Button>
              }
            />
            <Button data-testid="admin-logout-btn" onClick={async () => { await logout(); navigate("/"); }} variant="outline" className="rounded-full bg-transparent border-white/30 text-white hover:bg-white hover:text-brand-navy">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <StatCard Icon={Calendar} label="Demo Bookings" value={stats?.bookings ?? 0} color="#D90429" />
          <StatCard Icon={TrendingUp} label="New (unread)" value={stats?.new_bookings ?? 0} color="#FFC300" />
          <StatCard Icon={Users} label="Users" value={stats?.users ?? 0} color="#14213D" />
          <StatCard Icon={FileText} label="Paid Payments" value={stats?.payments_paid ?? 0} color="#D90429" />
        </div>

        <Tabs defaultValue="bookings" data-testid="admin-tabs">
          <TabsList className="bg-white border border-brand-navy/10 p-1.5 rounded-3xl h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className={tabTriggerClass}>Leads</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className={tabTriggerClass}>Users</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments" className={tabTriggerClass}>Payments</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses" className={tabTriggerClass}>Courses</TabsTrigger>
            <TabsTrigger value="ebooks" data-testid="tab-ebooks" className={tabTriggerClass}>E-Books</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog" className={tabTriggerClass}>Blog</TabsTrigger>
            {isSuper && <TabsTrigger value="reviews" data-testid="tab-reviews" className={tabTriggerClass}>Reviews</TabsTrigger>}
            {isSuper && <TabsTrigger value="immigration" data-testid="tab-immigration" className={tabTriggerClass}>Immigration</TabsTrigger>}
          </TabsList>

          <TabsContent value="payments" className="mt-6">
            <AdminPayments />
          </TabsContent>

          <TabsContent value="courses" className="mt-6">
            <AdminCourses isSuper={isSuper} />
          </TabsContent>

          <TabsContent value="ebooks" className="mt-6">
            <AdminEbookManager />
          </TabsContent>

          {isSuper && (
            <TabsContent value="reviews" className="mt-6">
              <AdminReviews />
            </TabsContent>
          )}

          {isSuper && (
            <TabsContent value="immigration" className="mt-6">
              <AdminImmigration />
            </TabsContent>
          )}

          <TabsContent value="bookings" className="mt-6">
            <div className="rounded-3xl bg-white border border-brand-navy/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-brand-muted-bg text-brand-navy">
                  <tr>
                    {["Name", "Contact", "Course", "Status", "Booked"].map((h) => (
                      <th key={h} className="text-left px-5 py-4 font-semibold text-xs uppercase tracking-widest">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-brand-navy/50">No leads yet.</td></tr>}
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-brand-navy/5">
                      <td className="px-5 py-4 font-semibold text-brand-navy">{b.name}</td>
                      <td className="px-5 py-4 text-brand-navy/70">
                        <div>{b.phone}</div>
                        <div className="text-xs text-brand-navy/50">{b.email}</div>
                      </td>
                      <td className="px-5 py-4"><Badge className="bg-brand-navy text-white hover:bg-brand-navy">{b.course}</Badge></td>
                      <td className="px-5 py-4">
                        <Select value={b.status || "new"} onValueChange={(v) => updateBookingStatus(b.id, v)}>
                          <SelectTrigger data-testid={`booking-status-${b.id}`} className="w-36 h-9 rounded-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {["new", "contacted", "converted", "dropped"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4 text-xs text-brand-navy/50">{new Date(b.created_at).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            <div className="rounded-3xl bg-white border border-brand-navy/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brand-muted-bg">
                  <tr>{["Name", "Contact", "User ID", "Auth", "Purpose", "Role", "Actions"].map((h) => <th key={h} className="text-left px-5 py-4 font-semibold text-xs uppercase tracking-widest text-brand-navy">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} data-testid={`user-row-${u.user_id}`} className="border-t border-brand-navy/5">
                      <td className="px-5 py-4 font-semibold text-brand-navy">{u.name}</td>
                      <td className="px-5 py-4 text-brand-navy/70">
                        <div>{u.email}</div>
                        <div className="text-xs text-brand-navy/50">{u.phone || "No phone"}</div>
                      </td>
                      <td className="px-5 py-4 text-xs text-brand-navy/50 font-mono">{u.user_id}</td>
                      <td className="px-5 py-4"><Badge variant="outline">{u.auth_provider}</Badge></td>
                      <td className="px-5 py-4 text-brand-navy/70">{u.survey_purpose || "—"}</td>
                      <td className="px-5 py-4"><Badge className={ROLE_BADGE[u.role] || ROLE_BADGE.user}>{ROLE_LABEL[u.role] || u.role}</Badge></td>
                      <td className="px-5 py-4">
                        {isSuper && u.user_id !== user.user_id ? (
                          <div className="flex items-center gap-2">
                            <Select value={["user", "sub_admin", "super_admin"].includes(u.role) ? u.role : "user"} onValueChange={(v) => changeRole(u.user_id, v)}>
                              <SelectTrigger data-testid={`role-select-${u.user_id}`} className="w-36 h-9 rounded-full text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="sub_admin">LNI Admin</SelectItem>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            {u.role !== "super_admin" && (
                              <Button
                                data-testid={`delete-user-btn-${u.user_id}`}
                                size="icon" variant="outline"
                                onClick={() => deleteUser(u.user_id)}
                                className="rounded-full h-9 w-9 shrink-0"
                                aria-label="Delete user"
                              >
                                <Trash2 className="w-4 h-4 text-brand-red" />
                              </Button>
                            )}
                            <ChangePasswordDialog
                              mode="admin"
                              userId={u.user_id}
                              targetLabel={u.name || u.email}
                              trigger={
                                <Button
                                  data-testid={`reset-password-btn-${u.user_id}`}
                                  size="icon" variant="outline"
                                  className="rounded-full h-9 w-9 shrink-0"
                                  aria-label={`Reset password for ${u.email}`}
                                >
                                  <KeyRound className="w-4 h-4 text-brand-navy" />
                                </Button>
                              }
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-brand-navy/40">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {!isSuper && <div className="p-4 bg-brand-gold/10 text-brand-navy text-xs" data-testid="users-readonly-note">You have view-only access to users. Only a Super Admin can change roles or remove users.</div>}
            </div>
          </TabsContent>

          <TabsContent value="blog" className="mt-6">
            <div className="mb-4 flex justify-between items-center">
              <div className="text-brand-navy font-semibold">{posts.length} posts</div>
              <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogTrigger asChild>
                  <Button data-testid="new-post-btn" onClick={openNewPost} className="rounded-full bg-brand-red hover:bg-brand-red-hover">
                    <Plus className="w-4 h-4 mr-1" /> New post
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl rounded-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-brand-navy">
                      {editing?.mode === "new" ? "New post" : `Edit: ${editing?.slug}`}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-3 mt-4">
                    <div><Label>Slug</Label><Input data-testid="post-slug" value={postForm.slug} disabled={editing?.mode === "edit"} onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })} placeholder="ielts-coaching-your-area" /></div>
                    <div><Label>Title</Label><Input data-testid="post-title" value={postForm.title} onChange={(e) => setPostForm({ ...postForm, title: e.target.value })} /></div>
                    <div><Label>Excerpt</Label><Textarea data-testid="post-excerpt" value={postForm.excerpt} onChange={(e) => setPostForm({ ...postForm, excerpt: e.target.value })} rows={2} /></div>
                    <div><Label>Body (markdown OK)</Label><Textarea data-testid="post-body" value={postForm.body} onChange={(e) => setPostForm({ ...postForm, body: e.target.value })} rows={10} /></div>
                    <div><Label>Hero image URL</Label><Input value={postForm.hero_image || ""} onChange={(e) => setPostForm({ ...postForm, hero_image: e.target.value })} /></div>
                    <div><Label>Tags (comma separated)</Label><Input value={postForm.tags} onChange={(e) => setPostForm({ ...postForm, tags: e.target.value })} placeholder="Rohini, IELTS, Delhi" /></div>
                    <div className="flex items-center gap-2">
                      <input id="pub" type="checkbox" checked={!!postForm.published} onChange={(e) => setPostForm({ ...postForm, published: e.target.checked })} />
                      <Label htmlFor="pub" className="cursor-pointer">Published</Label>
                    </div>
                    <Button data-testid="post-save-btn" onClick={savePost} disabled={savingPost} className="w-full rounded-full bg-brand-red hover:bg-brand-red-hover">
                      {savingPost ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save post"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {posts.map((p) => (
                <div key={p.slug} data-testid={`blog-row-${p.slug}`} className="rounded-2xl bg-white border border-brand-navy/10 p-5 card-hover">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-brand-red font-bold">{p.published ? "Published" : "Draft"}</div>
                      <div className="font-heading font-bold text-brand-navy mt-1">{p.title}</div>
                      <div className="text-sm text-brand-navy/60 mt-1 line-clamp-2">{p.excerpt}</div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button size="icon" variant="outline" onClick={() => openEditPost(p)} data-testid={`edit-post-${p.slug}`}><Edit3 className="w-4 h-4" /></Button>
                      <Button size="icon" variant="outline" onClick={() => deletePost(p.slug)} data-testid={`delete-post-${p.slug}`}><Trash2 className="w-4 h-4 text-brand-red" /></Button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">{(p.tags || []).map((t) => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
